import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import templatesAdminService, {
  type Template,
} from "../../../services/templatesAdminService";

export default function DetalhesTemplate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const carregarTemplate = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await templatesAdminService.buscar(parseInt(id));
        setTemplate(response.template);
      } catch (err) {
        console.error("Erro ao carregar template:", err);
        toast.error("Erro ao carregar template");
        navigate("/admin/templates");
      } finally {
        setLoading(false);
      }
    };

    void carregarTemplate();
  }, [id, navigate]);

  const handleToggleAtivo = async () => {
    if (!template) return;

    try {
      if (template.ativo) {
        await templatesAdminService.desativar(template.id);
        toast.success("Template desativado com sucesso!");
      } else {
        await templatesAdminService.ativar(template.id);
        toast.success("Template ativado com sucesso!");
      }
      // Recarregar
      const response = await templatesAdminService.buscar(template.id);
      setTemplate(response.template);
    } catch (err) {
      console.error("Erro:", err);
      toast.error("Erro ao alterar status do template");
    }
  };

  const handleDuplicar = async () => {
    if (!template) return;
    if (!confirm("Deseja duplicar este template?")) return;

    try {
      await templatesAdminService.duplicar(template.id);
      toast.success("Template duplicado com sucesso!");
      navigate("/admin/templates");
    } catch (err) {
      console.error("Erro:", err);
      toast.error("Erro ao duplicar template");
    }
  };

  const handleExcluir = async () => {
    if (!template) return;
    if (
      !confirm("Deseja excluir este template? Esta ação não pode ser desfeita.")
    )
      return;

    try {
      await templatesAdminService.excluir(template.id);
      toast.success("Template excluído com sucesso!");
      navigate("/admin/templates");
    } catch (err) {
      console.error("Erro:", err);
      toast.error(
        "Erro ao excluir template. Verifique se não há editais utilizando este template."
      );
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando template...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Template não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Botão Voltar */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/templates")}
          className="text-gray-600 hover:text-gray-900 transition font-medium"
        >
          ← Voltar para Templates
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {template.nome}
              </h1>
              <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded">
                v{template.versao}
              </span>
              <span
                className={`text-sm px-3 py-1 rounded font-medium ${
                  template.ativo
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {template.ativo ? "Ativo" : "Inativo"}
              </span>
            </div>
            {template.descricao && (
              <p className="text-gray-600">{template.descricao}</p>
            )}
          </div>

          <div className="flex gap-2">
            <Link
              to={`/admin/templates/${template.id}/editar`}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
            >
              Editar
            </Link>
            <button
              onClick={handleDuplicar}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
            >
              Duplicar
            </button>
            <button
              onClick={handleToggleAtivo}
              className={`px-4 py-2 rounded-md transition ${
                template.ativo
                  ? "bg-orange-600 text-white hover:bg-orange-700"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {template.ativo ? "Desativar" : "Ativar"}
            </button>
            <button
              onClick={handleExcluir}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
            >
              Excluir
            </button>
          </div>
        </div>
      </div>

      {/* Informações */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">Total de Campos</div>
          <div className="text-2xl font-bold text-gray-900">
            {Object.keys(template.schema_campos || {}).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">Editais Usando</div>
          <div className="text-2xl font-bold text-gray-900">
            {template.total_editais || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 mb-1">Criado por</div>
          <div className="text-lg font-semibold text-gray-900">
            {template.criado_por?.nome || "Sistema"}
          </div>
        </div>
      </div>

      {/* Preview dos Campos */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Preview do Formulário
        </h2>
        <div className="space-y-4">
          {Object.entries(template.schema_campos || {}).map(([key, campo]) => (
            <div
              key={key}
              className="border-b border-gray-200 pb-4 last:border-b-0"
            >
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {campo.label}
                  {campo.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {campo.type}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    {key}
                  </span>
                </div>
              </div>

              {/* Renderizar campo baseado no tipo */}
              {campo.type === "text" && (
                <input
                  type="text"
                  placeholder={campo.placeholder}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              )}

              {campo.type === "textarea" && (
                <textarea
                  placeholder={campo.placeholder}
                  disabled
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              )}

              {campo.type === "number" && (
                <input
                  type="number"
                  placeholder={campo.placeholder}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              )}

              {campo.type === "currency" && (
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">
                    R$
                  </span>
                  <input
                    type="number"
                    placeholder={campo.placeholder}
                    disabled
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              )}

              {campo.type === "select" && (
                <select
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                >
                  <option>Selecione uma opção</option>
                  {campo.options?.map((opt, idx) => (
                    <option key={idx}>{opt}</option>
                  ))}
                </select>
              )}

              {campo.type === "date" && (
                <input
                  type="date"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              )}

              {campo.type === "url" && (
                <input
                  type="url"
                  placeholder={campo.placeholder || "https://"}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              )}

              {/* Info adicional */}
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                {campo.maxLength && (
                  <span>Máximo: {campo.maxLength} caracteres</span>
                )}
                {campo.min !== undefined && <span>Mínimo: {campo.min}</span>}
                {campo.max !== undefined && <span>Máximo: {campo.max}</span>}
                {campo.placeholder && (
                  <span>Placeholder: "{campo.placeholder}"</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
